import {
  Box,
  Divider,
  FormControl,
  HStack,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { AddUser } from '@diamond/shared/types';
import {
  Button,
  Checkbox,
  showToast,
  Text,
  TextField,
} from '@diamond/shared/ui';
import { InviteUserSchema } from '@diamond/shared/validation';
import { useUserManagementData } from '@diamond/sol-buyer-context';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

export interface AddUserModalProps {
  onClose: () => void;
  refetch: () => void;
}

export function AddUserModal(props: AddUserModalProps) {
  const { shiptoData, postUser, setShiptoSize } = useUserManagementData();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(InviteUserSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      shiptos: [],
    },
  });

  const createUser = useMutation({
    mutationFn: async (payload: AddUser) =>
      postUser({ users: [payload] })
        .then(async () => {
          showToast(toast, 'success', 'Permintaan penambahan user berhasil!');
          props.refetch();
          props.onClose();
        })
        .catch((error) => {
          showToast(toast, 'error', error.message);
        }),
  });

  const onSubmit: SubmitHandler<AddUser> = (values) => {
    createUser.mutate(values);
  };

  const isInvalid = errors ? !!errors['shiptos'] : false;
  const errorMessage = errors ? errors['shiptos']?.message : '';

  return (
    <Box>
      <Divider borderWidth="1px" mb="4" mt={-2} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack mb="8">
          <Box mb={2}>
            <TextField
              name="email"
              label="Email"
              id="invite-user-email"
              placeholder="Masukkan Email"
              register={register}
              errors={errors}
            />
          </Box>
          <Box mb={2}>
            <TextField
              name="first_name"
              label="Nama Depan"
              id="invite-user-first-name"
              placeholder="Masukkan Nama Depan"
              maxLength={100}
              register={register}
              errors={errors}
            />
          </Box>
          <Box mb={4}>
            <TextField
              name="last_name"
              label="Nama Belakang"
              id="invite-user-last-name"
              placeholder="Masukkan Nama Belakang"
              maxLength={100}
              register={register}
              errors={errors}
            />
          </Box>
          <Box mt={4}>
            <Text my={2} fontWeight="medium" fontSize="md">
              Pilih Ship to:
            </Text>
            <InfiniteScroll
              dataLength={shiptoData.data.length}
              next={() => setShiptoSize(shiptoData.data.length + 10)}
              hasMore={shiptoData.data.length < shiptoData.pagination.rows}
              loader={<h4>Loading...</h4>}
              height={300}
            >
              <Box>
                <FormControl id="shiptos" isInvalid={isInvalid}>
                  {shiptoData?.data.map((val) => (
                    <Checkbox
                      key={val.id}
                      name="shiptos"
                      value={val.id}
                      register={register}
                      alignItems="start"
                    >
                      <Text mt={{ base: '-0.1rem', md: '-0.2rem' }}>
                        {val.name}
                      </Text>
                    </Checkbox>
                  ))}
                </FormControl>
              </Box>
            </InfiniteScroll>
            <Text color="red">{errorMessage as string}</Text>
          </Box>
        </Stack>

        <Divider borderWidth="1px" mb="4" mt={6} />
        <Box mt={6} mb={1}>
          <HStack w="full" alignContent="space-between">
            <Button onClick={props.onClose} w="full">
              Tutup
            </Button>
            <Button variant="solid" type="submit" w="full">
              Kirim Undangan
            </Button>
          </HStack>
        </Box>
      </form>
    </Box>
  );
}
